/**
 * EPIC 54 P2 — send an invited client user to their invitation, not into an
 * empty portal.
 *
 * Since P1 the server judges a person by their membership status, so somebody
 * who has been invited and not accepted is refused everything the portal
 * shows. Without this they would land on a dashboard of empty cards and error
 * toasts and have no way to work out why — the access was prepared, they had
 * simply never been given the door.
 *
 * The check asks the SERVER, because that is the only party that can answer:
 * an unaccepted member cannot read their own membership document.
 */

import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { listMyClientInvitations } from '../services/clientInvitations';

import { useAuth } from './AuthContext';

type State = 'checking' | 'member' | 'needs-acceptance';

export function RequireClientMembership({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<State>('checking');

  const onInvitationPage = location.pathname.startsWith('/client/invitation');

  useEffect(() => {
    let cancelled = false;
    // Staff are not members of anything and must not be redirected: an admin
    // opening the client portal is doing support, not onboarding.
    //
    // EPIC 54 P2 — everyone ELSE is asked, not only `client_user`. This used
    // to wave every other role straight through, so a WORKER invited to
    // manage their employer's orders reached the portal and was shown empty
    // screens: the one page that could have helped them was the one page
    // they were never sent to.
    const STAFF = ['admin', 'dispatcher', 'finance'];
    if (!user || STAFF.includes(user.role ?? '')) {
      setState('member');
      return;
    }
    void (async () => {
      try {
        const result = await listMyClientInvitations();
        if (cancelled) return;
        /**
         * BELONGING means an employer company, and a personal B2C record is
         * not one. `hasActive` counted both, so a company member with a
         * pending invitation who had once ordered something for their own
         * home sailed past this guard into a portal that refused them every
         * screen — the personal client made them look like they had already
         * joined.
         *
         * A B2C-only person is NOT sent to the invitation page: they have
         * nothing to accept, and there is nothing there for them. The two
         * questions are "does this account have an employer" and "is there
         * an invitation waiting", and both have to be asked.
         */
        const belongs = result.hasActiveCompany ?? result.hasActive;
        const invited = result.memberships.some((m) => m.status === 'invited');
        setState(!belongs && (!result.hasActive || invited) ? 'needs-acceptance' : 'member');
      } catch {
        // A failed check must not lock anybody out of a portal they may well
        // belong to; the server refuses each action on its own anyway.
        if (!cancelled) setState('member');
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (state === 'checking' && !onInvitationPage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state === 'needs-acceptance' && !onInvitationPage) {
    return <Navigate to="/client/invitation" replace />;
  }

  return <>{children}</>;
}
